import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import {
  CheckCircle2, AlertTriangle, ArrowLeft,
  RefreshCw, Copy, CheckCircle, Snowflake,
  Shield, Clock, ExternalLink,
} from 'lucide-react';
import { getOrderStatus } from '../services/api';
import ThawMeter from './ThawMeter';
import CopyRow from './CopyRow';
import '../styles/App.css';

// ── Status → ThawMeter step index ─────────────────────────────────────
const STEP_INDEX = {
  pending:    0,
  waiting:    0,
  confirming: 1,
  exchanging: 2,
  sending:    2,
  processing: 2,
  verifying:  2,
};

// ── Human-readable status messages ────────────────────────────────────
const STATUS_COPY = {
  pending:    'Waiting for your deposit…',
  waiting:    'Waiting for your deposit…',
  confirming: 'Deposit received — confirming on the network…',
  exchanging: 'Exchanging your assets…',
  sending:    'Sending your assets to the destination…',
  verifying:  'Verifying the transaction…',
  processing: 'Processing your swap…',
};

// ── Normalise any provider response into a single shape ───────────────
function normalise(data, orderId) {
  const base = {
    ...data,
    status:      (data.status || 'pending').toLowerCase(),
    from_asset:  data.from_asset  || data.fromAsset  || data.from  || '?',
    to_asset:    data.to_asset    || data.toAsset    || data.to    || '?',
    amount_expected:
      data.amount_expected || data.amountExpected ||
      data.fromAmount      || data.amount         || null,
    amount_received:
      data.amount_received || data.amountReceived ||
      data.toAmount        || null,
    to_amount:    data.to_amount    || data.toAmount    || null,
    shortId:      data.shortId      || data.short_id    || orderId?.slice(0, 8),
    destination_address:
      data.destination_address || data.destinationAddress ||
      data.toAddress           || null,
    refund_address:
      data.refund_address || data.refundAddress ||
      data.fromAddress    || null,
    out_hash: data.out_hash || data.outHash || data.txHash || null,
    message:  data.message  || null,
  };

  // ── Build deposit_instructions from whatever shape arrived ──────────
  //
  // Shape A — nested object (StealthEX, Exolix, 1Click):
  //   { deposit_instructions: { to_address, send_exactly, memo, ... } }
  //
  // Shape B — flat top-level (SwapKit, Mayan, Baltex):
  //   { deposit_address, amount_expected, memo, ... }
  //
  // Both map to the same internal shape so JSX never cares which provider.

  if (data.deposit_instructions || data.depositInstructions) {
    const di = data.deposit_instructions || data.depositInstructions;
    base.deposit_instructions = {
      to_address:
        di.to_address || di.address || di.toAddress ||
        di.depositAddress || null,
      send_exactly:
        di.send_exactly || di.amount || di.sendExactly ||
        base.amount_expected || null,
      memo:
        di.memo || di.paymentId || di.tag || di.depositMemo || null,
      warning:
        di.warning || di.note ||
        `Send only ${base.from_asset} to this address. Sending any other asset will result in permanent loss.`,
      expires_at:
        di.expires_at || di.expiresAt || di.expiration || null,
    };
  } else if (
    data.deposit_address || data.depositAddress ||
    data.inboundAddress  || data.targetAddress
  ) {
    const addr =
      data.deposit_address || data.depositAddress ||
      data.inboundAddress  || data.targetAddress;
    base.deposit_instructions = {
      to_address:   addr,
      send_exactly: base.amount_expected,
      memo:         data.memo || data.depositMemo || data.tag || null,
      warning:      `Send only ${base.from_asset} to this address. Sending any other asset will result in permanent loss.`,
      expires_at:   data.expires_at || data.expiresAt || null,
    };
  } else {
    base.deposit_instructions = null;
  }

  return base;
}

// ── Utilities ─────────────────────────────────────────────────────────
function fmtDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.toLocaleString();
}

function fmtAmount(n, asset) {
  if (!n && n !== 0) return null;
  const num = parseFloat(n);
  const str = isNaN(num) ? String(n) : num.toFixed(num < 0.001 ? 8 : 6).replace(/\.?0+$/, '');
  return asset ? `${str} ${asset}` : str;
}

// ── Main component ─────────────────────────────────────────────────────
const OrderStatus = () => {
  const { orderId }       = useParams();
  const [status, setStatus]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [countdown, setCountdown] = useState(30);

  const fetchStatus = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const data = await getOrderStatus(orderId);
      setStatus(normalise(data, orderId));
      setLastRefresh(new Date());
      setError(null);
      setCountdown(30);
    } catch (err) {
      setError(err.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Initial fetch
  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Auto-refresh every 30 s — stop when terminal status reached
  useEffect(() => {
    const TERMINAL = ['completed', 'success', 'finished', 'failed', 'refunded', 'expired'];
    if (status && TERMINAL.includes(status.status)) return;
    const iv = setInterval(() => fetchStatus(), 30_000);
    return () => clearInterval(iv);
  }, [fetchStatus, status]);

  // Countdown display
  useEffect(() => {
    const t = setInterval(() => setCountdown(c => c > 0 ? c - 1 : 30), 1_000);
    return () => clearInterval(t);
  }, []);

  // ── Loading ──────────────────────────────────────────────────────────
  if (loading && !status) {
    return (
      <div className="page">
        <div className="ice-glow" aria-hidden="true" />
        <div className="order-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <div className="loading">
            <div className="spinner" />
            <p>Fetching your order…</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (error && !status) {
    return (
      <div className="page">
        <div className="ice-glow" aria-hidden="true" />
        <div className="order-container">
          <Link to="/" className="back-link">
            <ArrowLeft size={16} /> Back to IceSwap
          </Link>
          <div className="card order-card">
            <div className="error-card">
              <AlertTriangle size={40} color="var(--red-500)" />
              <h2>Couldn't load order</h2>
              <p>{error}</p>
              <button
                className="btn btn-primary"
                style={{ width: 'auto', padding: '0.7rem 1.75rem' }}
                onClick={fetchStatus}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Derived state ────────────────────────────────────────────────────
  const st         = status?.status?.toLowerCase() || 'pending';
  const isComplete = ['completed', 'success', 'finished'].includes(st);
  const isFailed   = ['failed', 'refunded', 'expired'].includes(st);
  const isActive   = !isComplete && !isFailed;
  const stepIdx    = STEP_INDEX[st] ?? 0;

  const showDeposit =
    isActive &&
    status?.deposit_instructions?.to_address;

  const statusMsg =
    STATUS_COPY[st] ||
    status?.message ||
    `Status: ${st}`;

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="page">
      <div className="ice-glow" aria-hidden="true" />

      <div className="order-container">
        <Link to="/" className="back-link">
          <ArrowLeft size={16} /> Back to IceSwap
        </Link>

        <div className="card order-card">

          {/* ── Header ─────────────────────────────────────────────── */}
          <div className="order-header">
            <div className="privacy-pill">
              <Snowflake size={12} />
              IceSwap Order
            </div>

            <h1>
              Order <span className="order-id">
                #{status?.shortId || orderId?.slice(0, 8)}
              </span>
            </h1>

            {/* Progress meter */}
            {!isFailed && (
              <ThawMeter activeIndex={stepIdx} complete={isComplete} />
            )}

            {/* Status message */}
            <p className="status-message">{statusMsg}</p>

            {/* Refresh bar */}
            <div className="refresh-bar">
              {isActive && <span><Clock size={11} /> Refreshing in {countdown}s</span>}
              <button
                onClick={fetchStatus}
                disabled={loading}
                className="refresh-inline-btn"
                aria-label="Refresh"
              >
                <RefreshCw size={13} className={loading ? 'spin' : ''} />
              </button>
              {lastRefresh && (
                <span>
                  Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>

          {/* ── Deposit panel (pending / confirming) ───────────────── */}
          {showDeposit && (
            <div className="deposit-section">
              <h3>Send your deposit</h3>
              <p className="deposit-instruction">
                Send exactly{' '}
                <strong>
                  {fmtAmount(
                    status.deposit_instructions.send_exactly,
                    status.from_asset,
                  )}
                </strong>{' '}
                to the address below. Do not send a different amount.
              </p>

              {/* QR code */}
              <div className="qr-container">
                <div className="qr-code">
                  <QRCode
                    value={status.deposit_instructions.to_address}
                    size={160}
                    level="M"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="copy-field">
                <CopyRow
                  label="Deposit address"
                  value={status.deposit_instructions.to_address}
                />
              </div>

              {/* Memo (XRP, Stellar, TON, etc.) */}
              {status.deposit_instructions.memo && (
                <div className="copy-field">
                  <CopyRow
                    label="Memo / Tag (required)"
                    value={status.deposit_instructions.memo}
                  />
                </div>
              )}

              {/* Warning */}
              <div className="warning-box">
                <AlertTriangle size={16} />
                <div className="warning-content">
                  <strong>Important — </strong>
                  {status.deposit_instructions.warning}
                  {status.deposit_instructions.expires_at && (
                    <div className="expires">
                      Expires: {fmtDate(status.deposit_instructions.expires_at)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Confirming panel (deposit seen, not yet exchanged) ──── */}
          {st === 'confirming' && (
            <div className="notice" style={{ margin: '0 1.75rem 0' }}>
              <RefreshCw size={14} className="spin" style={{ flexShrink: 0 }} />
              <span>
                Your deposit has been detected. Waiting for network confirmations
                before the exchange begins.
              </span>
            </div>
          )}

          {/* ── Success panel ────────────────────────────────────────── */}
          {isComplete && (
            <div className="success-section">
              <div className="icon-badge">
                <CheckCircle2 size={30} />
              </div>
              <h3>Exchange complete!</h3>
              <p>
                {status.amount_received || status.to_amount
                  ? `You received ${fmtAmount(status.amount_received || status.to_amount, status.to_asset)}`
                  : `Your ${status.to_asset} has been sent.`}
              </p>
              {(status.out_hash || status.completion?.tx_hash) && (
                <TxRow hash={status.out_hash || status.completion.tx_hash} />
              )}
            </div>
          )}

          {/* ── Failed / refunded / expired panel ───────────────────── */}
          {isFailed && (
            <div className="failed-section">
              <div className="icon-badge">
                <AlertTriangle size={28} />
              </div>
              <h3>
                {st === 'refunded' ? 'Order refunded'
                  : st === 'expired' ? 'Order expired'
                  : 'Order failed'}
              </h3>
              <p>
                {st === 'refunded'
                  ? `Your ${status.from_asset} has been returned to your refund address.`
                  : st === 'expired'
                  ? 'This order expired before a deposit was received. No funds were taken.'
                  : `Something went wrong. If you sent funds, they will be returned to your refund address.`}
              </p>
            </div>
          )}

          {/* ── Order details grid ───────────────────────────────────── */}
          <div className="details-section">
            <h4>Order details</h4>

            <div className="details-grid">
              <div className="detail-item">
                <div className="detail-label">You send</div>
                <div className="detail-value">
                  {fmtAmount(status?.amount_expected, status?.from_asset) || `? ${status?.from_asset}`}
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-label">You receive</div>
                <div className="detail-value">
                  {isComplete
                    ? fmtAmount(status?.amount_received || status?.to_amount, status?.to_asset)
                    : status?.to_amount
                    ? fmtAmount(status.to_amount, status.to_asset)
                    : `Pending`}
                </div>
              </div>
            </div>

            <div className="address-details">
              {status?.destination_address && (
                <CopyRow
                  variant="inline"
                  label="Destination address"
                  value={status.destination_address}
                />
              )}
              {status?.refund_address && (
                <CopyRow
                  variant="inline"
                  label="Refund address"
                  value={status.refund_address}
                />
              )}
            </div>
          </div>

          {/* ── Privacy footer ───────────────────────────────────────── */}
          <div className="privacy-footer">
            <Shield size={13} />
            Non-custodial &middot; No KYC &middot; No logs
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Tx hash copy row ──────────────────────────────────────────────────
const TxRow = ({ hash }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    } catch {}
  };

  // Try to build a block explorer link from the hash format
  const explorerUrl = hash.startsWith('0x')
    ? `https://etherscan.io/tx/${hash}`
    : hash.length === 64
    ? null  // could be BTC or THORChain -- can't determine without chain context
    : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.875rem', flexWrap: 'wrap', justifyContent: 'center' }}>
      <div className="tx-row" onClick={copy} title="Click to copy">
        <span className="tx-label">TX</span>
        <span className="tx-value">
          {hash.slice(0, 16)}…{hash.slice(-8)}
        </span>
        {copied
          ? <CheckCircle size={13} color="var(--success)" />
          : <Copy size={13} />}
      </div>
      {explorerUrl && (
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="tx-row"
          style={{ textDecoration: 'none' }}
        >
          <ExternalLink size={13} />
          <span className="tx-label">Explorer</span>
        </a>
      )}
    </div>
  );
};

export default OrderStatus;
